// External-source results panel. Renders one card per source (DOC, PGPA,
// JOCPA) under the existing court-records list on PersonPage. Each card
// shows the latest scrape status + a button to enqueue a fresh scrape.
//
// The panel polls /api/external/jobs/:jobId every few seconds while any
// lookup is in `queued` or `running` so the UI updates without needing a
// full page reload. SSE wiring already broadcasts external_updated events
// — the polling is a backstop for missed messages.

import { useCallback, useMemo, useState } from "react";
import { DEACTIVATED_SOURCES, FIELD_LABELS_SHORT, missingRequiredFields, type RequiredField } from "../lib/externalSources";

export type ExternalSource =
  | "doc"
  | "pgpa"
  | "jocpa"
  | "mvc-surcharge"
  | "mvc-restoration"
  /** News-mention corroboration. Server-triggered after MCCS completes
   *  for high-newsworthy charges. NOT rendered in the source-cards grid
   *  below — it has its own dedicated NewsMentionsPanel section. */
  | "news";
// Mirrors server-side external_lookups.status values. "canceled" is set
// when a source is paused mid-flight (e.g. JOCPA deactivated server-side
// while rows were still queued/running); the row stays in the DB so the
// person's history is accurate, but the worker won't pick it up again.
export type ExternalStatus =
  | "queued"
  | "running"
  | "completed"
  | "error"
  | "blocked"
  | "canceled";

export interface ExternalLookup {
  id: number;
  person_job_id: string;
  source: ExternalSource;
  status: ExternalStatus;
  step: string | null;
  error_message: string | null;
  raw_json: string | null;
  artifact_paths: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  /** Server-attached classifier output for failed lookups (status =
   *  error/blocked). Always null for successful or in-flight rows.
   *  See src/server/lib/scrapeErrorClassifier.ts for the rule table. */
  errorClass?: ScrapeErrorClass | null;
  /** Optional: when the row was fetched via the /admin/recent endpoint
   *  (which joins to jobs), the person's name + DOB are attached so the
   *  scrape-admin page can label "Failed for Gary Maultsby" instead of
   *  just "Failed". The per-PersonPage panel doesn't need these (it
   *  already has the job context); they stay undefined there. */
  first_name?: string;
  last_name?: string;
  date_of_birth?: string;
}

/** Classifier shape — must match the server's ScrapeErrorClass exactly.
 *  Defined here too because client TS doesn't import from src/server. */
export interface ScrapeErrorClass {
  severity: "info" | "warn" | "alarm";
  label: string;
  explanation: string;
  retry: {
    likelihood: "good" | "poor" | "cooldown" | "unknown";
    advice: string;
  };
}

interface SourceMeta {
  source: ExternalSource;
  label: string;
  description: string;
  emptyMessage: string;
}

const SOURCES_RAW: SourceMeta[] = [
  {
    source: "doc",
    label: "NJ Department of Corrections",
    description: "Inmate Finder: current incarceration status, SBI #, sentence dates, facility.",
    emptyMessage: "Not currently in NJDOC custody (records of sentences ending >1 year ago are removed).",
  },
  {
    source: "pgpa",
    label: "NJ Superior Court (PROMIS/Gavel)",
    description: "Indictable / felony cases statewide. Excludes juvenile + expunged + impounded.",
    emptyMessage: "No Superior Court cases on file.",
  },
  {
    source: "jocpa",
    label: "Judgments of Conviction (JOCPA)",
    description: "Official PDF judgments: Conviction, Acquittal, Dismissal (June 2017–present).",
    emptyMessage: "No filed judgments since June 2017.",
  },
  {
    source: "mvc-surcharge",
    label: "NJ MVC: Surcharge Balance",
    description: "Outstanding NJ surcharges: DWI ($1k/yr × 3), 2C:40-26 driving-while-suspended, accumulated points. Often the biggest debt blocking license restoration.",
    emptyMessage: "No outstanding NJ surcharges on this license.",
  },
  {
    source: "mvc-restoration",
    label: "NJ MVC: Restoration Fees",
    description: "Outstanding restoration fees due to NJMVC before a suspended license can be restored.",
    emptyMessage: "No outstanding restoration fees.",
  },
];

const SOURCES: SourceMeta[] = SOURCES_RAW.filter(
  // The lib set is typed against SourceKey (no "news") since deactivated
  // never overlaps with the server-only "news" source. Cast is safe.
  (s) => !(DEACTIVATED_SOURCES as ReadonlySet<string>).has(s.source),
);

interface ExternalApiResponse {
  all: ExternalLookup[];
  latest: ExternalLookup[];
}

interface ExternalSourcesPanelProps {
  jobId: string;
  /** Optional MCCS-side context so the panel can render an MCCS card up
   *  front, alongside the external sources. When omitted, only the
   *  external sources show. Pass when the panel is the primary
   *  scrape-status surface (PersonPage); omit when it's a sidebar. */
  mccsStatus?: "queued" | "running" | "waiting_captcha" | "completed" | "error" | null;
  mccsRecordsCaptured?: number | null;
  mccsExpectedTotal?: number | null;
  mccsStep?: string | null;
  mccsErrorMessage?: string | null;
  mccsCompletedAt?: string | null;
  jobCreatedAt?: string | null;
  /** External-lookup data + actions, lifted from PersonPage's useExternalLookups
   *  hook. Was previously fetched + polled inside this component, but four
   *  components on the same page were doing the same fetch on independent
   *  cadences, so we consolidated to a single source-of-truth at PersonPage. */
  latest: Partial<Record<ExternalSource, ExternalLookup | null>>;
  loading: boolean;
  onEnqueue: (source: ExternalSource) => Promise<void>;
  /** Participant profile fields used by per-source field-gating: a
   *  source like MVC needs license_number to even attempt a search,
   *  so the action button shows "Add DL to scrape" instead of
   *  "Re-scrape" when DL is missing. Optional — when absent we fall
   *  back to the previous behavior (always show Re-scrape). */
  profile?: { license_number?: string | null; ssn?: string | null; zip_code?: string | null };
  /** Called when the user clicks an "Add <field> to scrape" prompt.
   *  Should open the profile-edit modal so they can fill in the
   *  missing field, then we can re-attempt the scrape. */
  onEditProfile?: () => void;
  /** Source-card filter toggle state, lifted from PersonPage. When a
   *  source key is in the set, that card renders an active-filter
   *  border AND the offense cards below filter to records from that
   *  source. Default is the empty set (no filter). Multiple keys can
   *  be active simultaneously (inclusive union).
   *
   *  Includes "mpa" for the MCCS card on top — clicking it filters to
   *  Municipal records. */
  activeSourceCardFilters?: Set<string>;
  onSourceCardToggle?: (key: string) => void;
}

const EMPTY_LATEST: Record<ExternalSource, ExternalLookup | null> = {
  doc: null,
  pgpa: null,
  jocpa: null,
  "mvc-surcharge": null,
  "mvc-restoration": null,
  // "news" intentionally absent from the SOURCES card grid below — it
  // surfaces in the dedicated NewsMentionsPanel instead. Still keyed
  // here so the Record<ExternalSource, …> type stays exhaustive.
  news: null,
};

export function ExternalSourcesPanel({
  jobId,
  njActivityDates,
  mccsStatus,
  mccsRecordsCaptured,
  mccsExpectedTotal,
  mccsStep,
  mccsErrorMessage,
  mccsCompletedAt,
  jobCreatedAt,
  latest: latestProp,
  loading,
  onEnqueue,
  profile,
  onEditProfile,
  activeSourceCardFilters,
  onSourceCardToggle,
}: ExternalSourcesPanelProps): JSX.Element {
  // Normalize the sparse map from the parent into the dense map shape the
  // existing rendering code expects (one slot per known source, null for
  // not-yet-tried). Cheap on every render, keeps internal logic untouched.
  const latest = useMemo<Record<ExternalSource, ExternalLookup | null>>(() => {
    const out = { ...EMPTY_LATEST };
    for (const k of Object.keys(latestProp) as ExternalSource[]) {
      const v = latestProp[k];
      if (v !== undefined) out[k] = v;
    }
    return out;
  }, [latestProp]);

  const [enqueuing, setEnqueuing] = useState<ExternalSource | null>(null);
  const [error, setError] = useState<string | null>(null);

  const enqueue = useCallback(
    async (source: ExternalSource) => {
      setEnqueuing(source);
      setError(null);
      try {
        await onEnqueue(source);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setEnqueuing(null);
      }
    },
    [onEnqueue],
  );

  // ─── Summary stats — surface progress + retry timing prominently ─────
  //
  // CRITICAL: these useMemo calls MUST run on every render, including
  // the initial loading render. React's hooks-order rule requires the
  // same hooks in the same order every time. The previous version had
  // these AFTER `if (loading) return`, which meant the first render
  // (loading=true) called 8 hooks and the second render (loading=false)
  // called 10. That triggered "Rendered more hooks than during the
  // previous render" (React error #310) and unmounted the whole page.
  // Hooks first, early returns second — always.
  const summary = useMemo(() => {
    const rows = SOURCES.map((m) => latest[m.source]).filter((r) => r !== null) as ExternalLookup[];
    const completed = rows.filter((r) => r.status === "completed").length;
    const errored = rows.filter((r) => r.status === "error" || r.status === "blocked").length;
    const running = rows.filter((r) => r.status === "running" || r.status === "queued").length;
    const totalSources = SOURCES.length + (mccsStatus !== undefined ? 1 : 0);
    const mccsDone =
      mccsStatus === "completed" || mccsStatus === "error" ? 1 : 0;
    return {
      completed: completed + mccsDone,
      errored: errored + (mccsStatus === "error" ? 1 : 0),
      running: running + (mccsStatus === "queued" || mccsStatus === "running" || mccsStatus === "waiting_captcha" ? 1 : 0),
      total: totalSources,
    };
  }, [latest, mccsStatus]);

  // The row most-likely to be auto-retried next: pick errored rows that
  // would still be eligible (job < 48h old, classifier likelihood good or
  // cooldown). Surface its ETA at the top.
  const nextAutoRetry = useMemo(() => {
    if (!jobCreatedAt) return null;
    const jobAgeMs = Date.now() - new Date(jobCreatedAt).getTime();
    if (jobAgeMs > 48 * 60 * 60_000) return null; // outside auto-retry window
    const candidateRows = (Object.values(latest) as Array<ExternalLookup | null>)
      .filter((r): r is ExternalLookup => {
        if (!r) return false;
        if (r.status !== "error" && r.status !== "blocked") return false;
        const ll = r.errorClass?.retry.likelihood;
        return ll === "good" || ll === "cooldown";
      });
    if (candidateRows.length === 0) return null;
    // Min-interval is 60 min from the failed row's created_at; auto-retry
    // pass runs every 30 min. Earliest possible retry per row:
    let earliest = Infinity;
    for (const r of candidateRows) {
      const lastAt = r.completed_at || r.created_at;
      if (!lastAt) continue;
      const eligibleAt = new Date(lastAt).getTime() + 60 * 60_000;
      if (eligibleAt < earliest) earliest = eligibleAt;
    }
    if (!Number.isFinite(earliest)) return null;
    const delta = earliest - Date.now();
    return { ts: earliest, delta };
  }, [latest, jobCreatedAt]);

  // Loading skeleton — early return AFTER all hooks are declared so
  // hook count stays stable across the loading→loaded transition.
  if (loading) {
    return (
      <section className="card p-4 mb-4">
        <div className="skeleton h-6 w-48 mb-3" />
        <div className="skeleton h-20 w-full" />
      </section>
    );
  }

  // Plain-English summary text — replaces the previous "X of Y complete · N running · M errored"
  // which was technical and hard for non-engineers to parse at a glance.
  const summaryText = (() => {
    const remaining = summary.total - summary.completed;
    if (remaining === 0) return `All ${summary.total} sources finished.`;
    if (summary.errored > 0 && summary.running === 0) {
      return `${summary.completed} of ${summary.total} done · ${summary.errored} need a closer look.`;
    }
    if (summary.running > 0) {
      return `${summary.completed} of ${summary.total} done · ${summary.running} still searching.`;
    }
    return `${summary.completed} of ${summary.total} done.`;
  })();

  return (
    <section className="card p-4 mb-4">
      <header className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
        <h3 className="text-[15px] font-semibold">Public-records sources</h3>
        <span className="text-[11px] opacity-60">{summaryText}</span>
      </header>

      {/* Progress bar */}
      <div className="mb-3" style={{ height: 6, background: "var(--color-bg-card-hi)", borderRadius: 999, overflow: "hidden" }}>
        <div
          style={{
            height: "100%",
            width: `${summary.total === 0 ? 0 : Math.round((summary.completed / summary.total) * 100)}%`,
            background:
              summary.errored > 0
                ? "linear-gradient(90deg, var(--color-sage-hi), var(--color-amber-hi))"
                : "var(--color-sage-hi)",
            transition: "width 380ms ease",
          }}
        />
      </div>

      {/* Plain-English auto-retry banner. Shown when at least one source
          failed AND it's eligible for the 48-hour auto-retry pass.
          Replaces the previous "Auto-retry pending · Next eligible run
          ~5 min · Errored rows auto-re-enqueue every 30 min for up to
          48h" which was technically accurate but jargon-heavy for
          non-engineers. */}
      {nextAutoRetry && (
        <div
          className="text-[12px] mb-3 px-3 py-2 rounded"
          style={{
            background: "rgba(212,175,55,0.08)",
            border: "1px solid rgba(212,175,55,0.25)",
            color: "var(--color-text-muted)",
          }}
        >
          <strong style={{ color: "var(--color-gold-hi)" }}>We'll try again automatically.</strong>{" "}
          {nextAutoRetry.delta > 0
            ? `Next attempt in about ${formatRelative(nextAutoRetry.delta)}.`
            : `Next attempt within 30 min.`}{" "}
          <span style={{ opacity: 0.7 }}>
            Failed sources keep retrying for 48 hours after the person was added, no action needed.
          </span>
        </div>
      )}

      {error && (
        <div className="text-xs text-[color:var(--color-rose-hi)] mb-2">{error}</div>
      )}
      <MvcDebtBanner
        surcharge={latest["mvc-surcharge"]}
        restoration={latest["mvc-restoration"]}
      />

      {/* MCCS card — synthesized from job-level fields, not external_lookups.
          Renders ONLY when mccsStatus was passed (callers can opt out). */}
      {mccsStatus !== undefined && (
        <div className="mb-3">
          <McccsCard
            status={mccsStatus}
            recordsCaptured={mccsRecordsCaptured ?? null}
            expectedTotal={mccsExpectedTotal ?? null}
            step={mccsStep ?? null}
            errorMessage={mccsErrorMessage ?? null}
            completedAt={mccsCompletedAt ?? null}
            isFilterActive={activeSourceCardFilters?.has("mpa") ?? false}
            onToggleFilter={onSourceCardToggle ? () => onSourceCardToggle("mpa") : undefined}
          />
        </div>
      )}

      <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
        {SOURCES.map((meta) => (
          <SourceCard
            key={meta.source}
            meta={meta}
            row={latest[meta.source]}
            enqueuing={enqueuing === meta.source}
            onEnqueue={() => enqueue(meta.source)}
            njActivityDates={njActivityDates}
            jobCreatedAt={jobCreatedAt ?? null}
            profile={profile}
            onEditProfile={onEditProfile}
            isFilterActive={activeSourceCardFilters?.has(meta.source) ?? false}
            onToggleFilter={onSourceCardToggle ? () => onSourceCardToggle(meta.source) : undefined}
          />
        ))}
      </div>
    </section>
  );
}

/** Format a millisecond delta as a human "in X min" string. */
function formatRelative(deltaMs: number): string {
  if (deltaMs < 0) return "now";
  const mins = Math.round(deltaMs / 60_000);
  if (mins < 60) return `in ${mins} min`;
  const hrs = Math.round(mins / 60);
  return `in ~${hrs}h`;
}

// ─── MCCS card ────────────────────────────────────────────────────────
//
// Synthesized from job-level fields (status, step, error_message, record
// counts) since MCCS results live in the records table, not the
// external_lookups table. Same visual language as the external source
// cards so the whole panel reads as one coherent view.

function McccsCard({
  status,
  recordsCaptured,
  expectedTotal,
  step,
  errorMessage,
  completedAt,
  isFilterActive,
  onToggleFilter,
}: {
  status: "queued" | "running" | "waiting_captcha" | "completed" | "error" | null;
  recordsCaptured: number | null;
  expectedTotal: number | null;
  step: string | null;
  errorMessage: string | null;
  completedAt: string | null;
  isFilterActive?: boolean;
  onToggleFilter?: () => void;
}): JSX.Element {
  const isRunning = status === "queued" || status === "running" || status === "waiting_captcha";
  const isError = status === "error";
  const isComplete = status === "completed";
  const captured = recordsCaptured ?? 0;
  const expected = expectedTotal ?? 0;
  const partial = isComplete && expected > 0 && captured < expected;
  const pillStatus =
    status === "waiting_captcha" ? "running" :
    status === "running" || status === "queued" ? "running" :
    status === "error" ? "error" :
    status === "completed" ? "completed" : null;
  return (
    <div
      className="rounded-lg p-3 flex flex-col gap-2"
      onClick={onToggleFilter}
      role={onToggleFilter ? "button" : undefined}
      tabIndex={onToggleFilter ? 0 : undefined}
      onKeyDown={onToggleFilter ? (e) => {
        if (e.key === "Enter" || e.key === " ") {
          if (e.target === e.currentTarget) {
            e.preventDefault();
            onToggleFilter();
          }
        }
      } : undefined}
      title={onToggleFilter ? (isFilterActive ? "Click to clear this source filter" : "Click to filter offense cards to Municipal Court records") : undefined}
      style={{
        background: "var(--color-bg-card-hi)",
        border: isFilterActive
          ? "2px solid var(--color-gold-hi)"
          : `1px solid ${isError ? "rgba(232,100,100,0.45)" : partial ? "rgba(255,200,80,0.45)" : "var(--color-border)"}`,
        boxShadow: isFilterActive ? "0 0 0 2px rgba(212,175,55,0.18)" : "none",
        transition: "border-color 120ms, box-shadow 120ms",
        cursor: onToggleFilter ? "pointer" : "default",
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold">NJ Municipal Court (MCCS)</div>
          <div className="text-[11px] opacity-70 leading-snug">
            Every municipal-court complaint statewide, traffic, DPs, DV contempt, ticket-only payments.
          </div>
        </div>
        <StatusPill status={pillStatus ?? undefined} />
      </div>
      {isRunning && (
        <div className="text-[11px] opacity-80 italic">
          {status === "waiting_captcha" ? "Waiting on CAPTCHA solver…" : (step || "Searching…")}
          {expected > 0 && (
            <span className="ml-2 opacity-60">
              ({captured} of {expected} captured so far)
            </span>
          )}
        </div>
      )}
      {isError && (
        <div className="text-[12px]" style={{ color: "var(--color-rose-hi)" }}>
          <strong>Error:</strong> {errorMessage || "Search failed."}{" "}
          <span style={{ opacity: 0.75 }}>Click Retry on the cards/table view to re-run.</span>
        </div>
      )}
      {isComplete && (
        <div className="text-[12px]">
          <strong style={{ color: partial ? "var(--color-amber-hi)" : "var(--color-sage-hi)" }}>
            {partial ? "Partial capture" : "Complete"}
          </strong>
          {" — "}
          <strong>{captured}</strong> {captured === 1 ? "record" : "records"} captured
          {partial && <> · <strong>{expected - captured}</strong> still missing: auto-retry will re-attempt</>}
          {expected > 0 && !partial && <> (site reported {expected} total)</>}.
        </div>
      )}
      <div className="text-[10px] opacity-50 mt-1">
        {completedAt && `Last run: ${new Date(completedAt).toLocaleString()}`}
      </div>
    </div>
  );
}

// ─── MVC Debt Banner ───────────────────────────────────────────────────
//
// Combined surcharge + restoration-fee summary. Shown above the per-
// source cards when EITHER lookup completed. Quick scan for "what does
// this client owe NJMVC before they can drive again?" — directly aligned
// with NJRC's fine-removal mission.

function MvcDebtBanner({
  surcharge,
  restoration,
}: {
  surcharge: ExternalLookup | null;
  restoration: ExternalLookup | null;
}): JSX.Element | null {
  // Skip the banner entirely until at least one MVC lookup has FINISHED
  // — running/queued state is conveyed by the per-source cards below.
  const surchargeComplete = surcharge?.status === "completed";
  const restorationComplete = restoration?.status === "completed";
  if (!surchargeComplete && !restorationComplete) return null;

  const surchargeData = surchargeComplete ? safeParseRaw(surcharge!.raw_json) : null;
  const restorationData = restorationComplete ? safeParseRaw(restoration!.raw_json) : null;

  const surchargeOwed = (surchargeData?.totalOwed as number | null | undefined) ?? null;
  const restorationDue = (restorationData?.totalDue as number | null | undefined) ?? null;
  const surchargeNoMatch = Boolean(surchargeData?.noMatch);
  const restorationLoginFailed = Boolean(restorationData?.loginFailed);

  const knownAmounts = [
    surchargeOwed !== null ? surchargeOwed : null,
    restorationDue !== null ? restorationDue : null,
  ].filter((n): n is number => n !== null);

  const total = knownAmounts.reduce((a, b) => a + b, 0);
  const onPaymentPlan = Boolean(surchargeData?.onPaymentPlan);

  // Color tier: red >= $1k, amber >= $100, green for $0, gray for unknown.
  const accent =
    total >= 1000
      ? "var(--color-rose-hi, #f59e9e)"
      : total >= 100
      ? "var(--color-amber-hi, #ffd166)"
      : knownAmounts.length > 0
      ? "var(--color-emerald-hi, #8ed1a8)"
      : "var(--color-text-dim)";

  return (
    <div
      className="mb-3 rounded-lg p-4"
      style={{
        background: `linear-gradient(135deg, ${accent}10, color-mix(in oklab, var(--color-text) 3%, transparent))`,
        border: `1px solid ${accent}55`,
      }}
    >
      <div className="flex items-baseline justify-between mb-2 gap-3 flex-wrap">
        <div className="flex items-baseline gap-3">
          <span
            className="text-[10px] font-semibold uppercase"
            style={{ letterSpacing: "0.18em", color: accent }}
          >
            NJ MVC debt
          </span>
          {onPaymentPlan && (
            <span
              className="text-[10px] uppercase rounded px-2 py-0.5"
              style={{
                letterSpacing: "0.14em",
                color: "var(--color-amber-hi, #ffd166)",
                background: "rgba(255,209,102,0.10)",
                border: "1px solid rgba(255,209,102,0.4)",
              }}
            >
              Installment plan
            </span>
          )}
        </div>
        <span className="text-[10px] opacity-60">
          before this client can drive legally
        </span>
      </div>
      <div className="flex items-baseline gap-4 flex-wrap">
        <div
          className="font-semibold tabular"
          style={{
            fontFamily: "Fraunces, var(--font-serif), serif",
            fontSize: "clamp(28px, 4vw, 42px)",
            color: accent,
            lineHeight: 1,
          }}
        >
          {knownAmounts.length === 0 ? "—" : `$${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
        </div>
        <div className="flex flex-col gap-1 text-[12px] opacity-85">
          {surchargeComplete && (
            <div>
              <span className="opacity-60 mr-2">Surcharges:</span>
              {surchargeNoMatch
                ? "no records / lookup failed"
                : surchargeOwed === null
                ? "amount unparsed (see card below)"
                : surchargeOwed === 0
                ? "$0: paid in full"
                : `$${surchargeOwed.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            </div>
          )}
          {restorationComplete && (
            <div>
              <span className="opacity-60 mr-2">Restoration fees:</span>
              {restorationLoginFailed
                ? "login failed (check DL/SSN/ZIP)"
                : restorationDue === null
                ? "amount unparsed (see card below)"
                : restorationDue === 0
                ? "$0 due"
                : `$${restorationDue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            </div>
          )}
          {!surchargeComplete && (
            <div className="opacity-60 italic">Surcharges: not yet checked (need DL on intake)</div>
          )}
          {!restorationComplete && (
            <div className="opacity-60 italic">Restoration fees: not yet checked (needs DL + SSN + ZIP)</div>
          )}
        </div>
      </div>
    </div>
  );
}

function safeParseRaw(json: string | null): Record<string, unknown> | null {
  if (!json) return null;
  try {
    const parsed = JSON.parse(json);
    return typeof parsed === "object" && parsed !== null ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function SourceCard({
  meta,
  row,
  enqueuing,
  onEnqueue,
  njActivityDates,
  jobCreatedAt,
  profile,
  onEditProfile,
  isFilterActive,
  onToggleFilter,
}: {
  meta: SourceMeta;
  row: ExternalLookup | null;
  enqueuing: boolean;
  onEnqueue: () => void;
  njActivityDates?: Date[];
  jobCreatedAt: string | null;
  profile?: { license_number?: string | null; ssn?: string | null; zip_code?: string | null };
  onEditProfile?: () => void;
  isFilterActive?: boolean;
  onToggleFilter?: () => void;
}): JSX.Element {
  // What fields are missing on the participant that this source needs?
  // Only computed when caller passed `profile`. Empty array → no
  // gating; the normal Run / Re-scrape button shows.
  const missingForThisSource = profile ? missingRequiredFields(meta.source, profile) : [];
  const isActive = row?.status === "queued" || row?.status === "running";
  const isComplete = row?.status === "completed";
  // True only when the scrape genuinely failed at the source side
  // (worker crash, source blocked, network failure, etc.). When the
  // profile is missing fields this source needs, the "error" the
  // worker logged is really a "we couldn't run because we lack X"
  // situation — show that as Need-Info, not Error.
  const needsInfo = missingForThisSource.length > 0;
  const isError = !needsInfo && (row?.status === "error" || row?.status === "blocked");
  // Pill in the top-right. Override the row status when this is
  // really a needs-info situation so the LSC reads it as actionable.
  const pillStatus: PillStatus | undefined = needsInfo ? "needs-info" : row?.status;

  // Parse the raw_json result. Each scraper has its own shape so we render
  // the relevant subset per source.
  const parsed = useParsedResult(row);

  return (
    <div
      // Anchor target for SourceStatusStrip pills. scroll-mt-24 pushes the
      // top of the card down so it doesn't sit flush against the sticky
      // page header when scrolled to via the "#source-…" hash.
      //
      // The WHOLE card is the click target for the filter toggle. Nested
      // interactive elements (Run/Re-scrape button, expand toggle, the
      // Add-info "..." action) all wrap their onClick to stopPropagation
      // so a click on those doesn't accidentally trip the filter too.
      id={`source-${meta.source}`}
      className="rounded-lg p-3 flex flex-col gap-2 scroll-mt-24"
      onClick={onToggleFilter}
      role={onToggleFilter ? "button" : undefined}
      tabIndex={onToggleFilter ? 0 : undefined}
      onKeyDown={onToggleFilter ? (e) => {
        if (e.key === "Enter" || e.key === " ") {
          // Only fire when the focused element is the card itself, not
          // a button inside. Without this, keyboard activating an inner
          // button would also toggle the filter.
          if (e.target === e.currentTarget) {
            e.preventDefault();
            onToggleFilter();
          }
        }
      } : undefined}
      title={onToggleFilter ? (isFilterActive ? "Click to clear this source filter" : "Click to filter offense cards to this source") : undefined}
      style={{
        background: "var(--color-bg-card-hi)",
        border: isFilterActive
          ? "2px solid var(--color-gold-hi)"
          : "1px solid var(--color-border)",
        boxShadow: isFilterActive ? "0 0 0 2px rgba(212,175,55,0.18)" : "none",
        transition: "border-color 120ms, box-shadow 120ms",
        cursor: onToggleFilter ? "pointer" : "default",
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold truncate">{meta.label}</div>
          <div className="text-[11px] opacity-70 leading-snug">{meta.description}</div>
        </div>
        <StatusPill status={pillStatus} />
      </div>

      {needsInfo && (
        <div
          className="text-[11.5px] leading-snug rounded px-2 py-1.5"
          style={{
            background: "rgba(255, 200, 80, 0.10)",
            border: "1px solid rgba(255, 200, 80, 0.32)",
            color: "var(--color-text)",
          }}
        >
          <strong style={{ color: "var(--color-amber-hi)" }}>Need info:</strong>{" "}
          this source needs {missingForThisSource.map((f) => FIELD_LABELS_SHORT[f]).join(" + ")} before it can run.
        </div>
      )}
      {row?.step && !isError && !needsInfo && (
        <div className="text-[11px] opacity-80 italic">{row.step}</div>
      )}
      {isError && (
        <FailureExplainer
          rawMessage={row?.error_message ?? null}
          step={row?.step ?? null}
          errorClass={row?.errorClass ?? null}
        />
      )}
      {/* Per-row auto-retry hint when applicable. Computed identically
          to the panel-level estimate — eligible at row.completed_at +
          60min (or row.created_at if no completion) AND only if the job
          is still inside the 48h auto-retry window AND the classifier
          rated this error as good/cooldown. */}
      {isError && row && jobCreatedAt && (() => {
        const jobAgeMs = Date.now() - new Date(jobCreatedAt).getTime();
        if (jobAgeMs > 48 * 60 * 60_000) return null;
        const ll = row.errorClass?.retry.likelihood;
        if (ll !== "good" && ll !== "cooldown") return null;
        const lastAt = row.completed_at || row.created_at;
        if (!lastAt) return null;
        const eligibleAt = new Date(lastAt).getTime() + 60 * 60_000;
        const delta = eligibleAt - Date.now();
        return (
          <div
            className="text-[10.5px] px-2 py-1 rounded"
            style={{
              background: "rgba(212,175,55,0.10)",
              border: "1px solid rgba(212,175,55,0.25)",
              color: "var(--color-text-muted)",
            }}
          >
            <strong style={{ color: "var(--color-gold-hi)" }}>↻ Auto-retry</strong>{" "}
            {delta > 0 ? `eligible in ${formatRelative(delta)}` : `eligible now (next pass within 30 min)`}.
          </div>
        );
      })()}
      {isComplete && parsed && <SourceSummary source={meta.source} parsed={parsed} emptyMessage={meta.emptyMessage} njActivityDates={njActivityDates} />}
      {!row && (
        <div className="text-[11px] opacity-60 italic">Not yet scraped.</div>
      )}

      <div className="flex items-center justify-between gap-2 mt-1" onClick={(e) => e.stopPropagation()}>
        <div className="text-[10px] opacity-50">
          {row?.completed_at && `Last run: ${new Date(row.completed_at).toLocaleString()}`}
          {!row?.completed_at && row?.created_at && `Queued: ${new Date(row.created_at).toLocaleString()}`}
        </div>
        <RetryButton
          isActive={isActive}
          enqueuing={enqueuing}
          hasRow={Boolean(row)}
          onClick={onEnqueue}
          retryLikelihood={row?.errorClass?.retry.likelihood}
          isError={isError}
          missingFields={missingForThisSource}
          onEditProfile={onEditProfile}
        />
      </div>
    </div>
  );
}

/** Renders a failed-lookup explainer block: raw error text + classifier
 *  output (severity chip, plain-English explanation, retry advice). The
 *  raw text is collapsed by default — click to expand for engineering
 *  debugging. The classifier output is the human-readable summary. */
function FailureExplainer({
  rawMessage,
  step,
  errorClass,
}: {
  rawMessage: string | null;
  step: string | null;
  errorClass: ScrapeErrorClass | null;
}): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const fallback = rawMessage ?? step ?? "(no message)";
  // No classifier means server didn't run it (older row) — show raw msg.
  if (!errorClass) {
    return (
      <div className="text-[11px] text-[color:var(--color-rose-hi)] break-words">
        {fallback}
      </div>
    );
  }
  const sevColor =
    errorClass.severity === "alarm"
      ? "var(--color-rose-hi)"
      : errorClass.severity === "warn"
        ? "var(--color-amber-hi)"
        : "var(--color-sage-hi)";
  const retryPalette: Record<ScrapeErrorClass["retry"]["likelihood"], { label: string; bg: string; fg: string }> = {
    good: { label: "Retry likely succeeds", bg: "rgba(120,220,150,0.16)", fg: "var(--color-sage-hi)" },
    poor: { label: "Retry won't help", bg: "rgba(255,120,120,0.14)", fg: "var(--color-rose-hi)" },
    cooldown: { label: "Wait 30+ min before retry", bg: "rgba(232,160,80,0.18)", fg: "var(--color-amber-hi)" },
    unknown: { label: "Try once, escalate if it persists", bg: "color-mix(in oklab, var(--color-text) 6%, transparent)", fg: "var(--color-text-muted)" },
  };
  const retry = retryPalette[errorClass.retry.likelihood];
  return (
    <div
      className="rounded p-2 flex flex-col gap-1.5"
      style={{
        background: "color-mix(in oklab, var(--color-text) 3%, transparent)",
        border: `1px solid ${sevColor}`,
      }}
    >
      <div className="flex items-center gap-1.5 flex-wrap">
        <span
          className="text-[9px] font-bold uppercase rounded px-1.5 py-0.5"
          style={{
            color: sevColor,
            background: "rgba(0,0,0,0.2)",
            letterSpacing: "0.1em",
            fontFamily: "var(--font-mono)",
          }}
        >
          {errorClass.label}
        </span>
        <span
          className="text-[9px] font-semibold rounded px-1.5 py-0.5"
          style={{
            color: retry.fg,
            background: retry.bg,
            fontFamily: "var(--font-mono)",
            letterSpacing: "0.04em",
          }}
        >
          {retry.label}
        </span>
      </div>
      <div className="text-[11px] leading-snug" style={{ color: "var(--color-text)" }}>
        {errorClass.explanation}
      </div>
      <div className="text-[11px] leading-snug opacity-80 italic">
        {errorClass.retry.advice}
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setExpanded((x) => !x);
        }}
        className="self-start text-[10px] opacity-50 hover:opacity-100 transition"
        style={{ fontFamily: "var(--font-mono)" }}
      >
        {expanded ? "Hide raw message ↑" : "Show raw message ↓"}
      </button>
      {expanded && (
        <pre
          className="text-[10px] opacity-70 whitespace-pre-wrap break-words rounded p-1.5 mt-0.5"
          style={{
            background: "rgba(0,0,0,0.25)",
            fontFamily: "var(--font-mono)",
            maxHeight: 120,
            overflow: "auto",
          }}
        >
          {fallback}
        </pre>
      )}
    </div>
  );
}

/** Per-source retry button. The label adapts to retry-likelihood so the
 *  user knows at a glance whether clicking is worth their time. */
function RetryButton({
  isActive,
  enqueuing,
  hasRow,
  onClick,
  retryLikelihood,
  isError,
  missingFields,
  onEditProfile,
}: {
  isActive: boolean;
  enqueuing: boolean;
  hasRow: boolean;
  onClick: () => void;
  retryLikelihood: ScrapeErrorClass["retry"]["likelihood"] | undefined;
  isError: boolean;
  missingFields: ReadonlyArray<RequiredField>;
  onEditProfile?: () => void;
}): JSX.Element {
  // If the participant is missing fields this source needs, the action
  // button changes role: instead of "Re-scrape" / "Retry" (which would
  // just fail again with the same missing-field error), we show
  // "Add DL to scrape" and route the click to the profile-edit modal.
  // Once the field is filled, the source's natural retry logic takes
  // over on the next eligible cycle.
  if (missingFields.length > 0 && !isActive && !enqueuing) {
    const labelText = missingFields.length === 1
      ? `Add ${FIELD_LABELS_SHORT[missingFields[0]!]} to scrape`
      : `Add ${missingFields.map((f) => FIELD_LABELS_SHORT[f]).join(" + ")} to scrape`;
    return (
      <button
        type="button"
        onClick={onEditProfile}
        disabled={!onEditProfile}
        title={`This source needs ${missingFields.map((f) => FIELD_LABELS_SHORT[f]).join(" + ")} on file before it can search. Click to add.`}
        className="text-[11px] font-semibold rounded px-2 py-1 transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        style={{
          background: "rgba(232,160,80,0.14)",
          border: "1px solid rgba(232,160,80,0.45)",
          color: "var(--color-amber-hi)",
        }}
      >
        ✎ {labelText}
      </button>
    );
  }

  let label: string;
  let palette: { bg: string; border: string; fg: string };
  if (isActive || enqueuing) {
    label = "Running…";
    palette = { bg: "var(--color-bg-card)", border: "var(--color-border)", fg: "var(--color-text)" };
  } else if (isError && retryLikelihood === "good") {
    label = "Retry now";
    palette = { bg: "rgba(120,220,150,0.16)", border: "rgba(120,220,150,0.4)", fg: "var(--color-sage-hi)" };
  } else if (isError && retryLikelihood === "cooldown") {
    label = "Retry anyway";
    palette = { bg: "rgba(232,160,80,0.16)", border: "rgba(232,160,80,0.4)", fg: "var(--color-amber-hi)" };
  } else if (isError && retryLikelihood === "poor") {
    label = "Retry (unlikely to help)";
    palette = { bg: "rgba(255,120,120,0.10)", border: "rgba(255,120,120,0.35)", fg: "var(--color-rose-hi)" };
  } else if (isError) {
    label = "Retry";
    palette = { bg: "var(--color-bg-card)", border: "var(--color-border)", fg: "var(--color-text)" };
  } else {
    label = hasRow ? "Re-scrape" : "Run";
    palette = { bg: "var(--color-bg-card)", border: "var(--color-border)", fg: "var(--color-text)" };
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isActive || enqueuing}
      className="text-[11px] font-semibold rounded px-2 py-1 transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
      style={{ background: palette.bg, border: `1px solid ${palette.border}`, color: palette.fg }}
    >
      {label}
    </button>
  );
}

type PillStatus = ExternalStatus | "needs-info";

function StatusPill({ status }: { status: PillStatus | undefined }): JSX.Element {
  const palette: Record<PillStatus, { bg: string; text: string; label: string }> = {
    queued: { bg: "rgba(255, 200, 80, 0.18)", text: "var(--color-amber-hi)", label: "Queued" },
    running: { bg: "rgba(120, 180, 255, 0.18)", text: "var(--color-blue-hi)", label: "Running" },
    completed: { bg: "rgba(120, 220, 150, 0.18)", text: "var(--color-sage-hi)", label: "Done" },
    error: { bg: "rgba(255, 120, 120, 0.18)", text: "var(--color-rose-hi)", label: "Error" },
    blocked: { bg: "rgba(255, 120, 80, 0.18)", text: "var(--color-rose-hi)", label: "Blocked: retrying" },
    canceled: { bg: "rgba(160, 160, 160, 0.18)", text: "var(--color-text-dim)", label: "Canceled" },
    // Amber, same family as the "Add DL #" button — reads as
    // "your turn, supply info" rather than a true failure.
    "needs-info": { bg: "rgba(255, 200, 80, 0.20)", text: "var(--color-amber-hi)", label: "Need info" },
  };
  if (!status) {
    return <span className="text-[10px] opacity-50">—</span>;
  }
  // Defensive fallback: the server's status string is loose-typed at the
  // edge (ServerEventExternalLookup.status: string) so a value outside the
  // union can sneak through. Render an "—" rather than crashing the page
  // — which is what previously blanked PersonPage on rows we'd marked
  // 'canceled' before this status existed in the client palette.
  const p = palette[status];
  if (!p) {
    return <span className="text-[10px] opacity-50" title={`unknown status: ${status}`}>—</span>;
  }
  return (
    <span
      className="text-[10px] font-semibold uppercase tracking-wide rounded px-2 py-0.5 whitespace-nowrap"
      style={{ background: p.bg, color: p.text }}
    >
      {p.label}
    </span>
  );
}

interface ParsedResult {
  totalRows: number;
  noResults: boolean;
  rows: Array<Record<string, unknown>>;
  capturedAt: string;
}

function useParsedResult(row: ExternalLookup | null): ParsedResult | null {
  if (!row?.raw_json) return null;
  try {
    const obj = JSON.parse(row.raw_json) as Record<string, unknown>;
    // MVC sources have a totally different shape — surcharge has
    // totalOwed + lines, restoration has totalDue + lines. Treat each
    // line as a "row" so the existing card scaffolding works, and let
    // summarizeRow render the per-line description + amount.
    if ("totalOwed" in obj || "totalDue" in obj) {
      const lines = (obj.lines as Array<Record<string, unknown>>) ?? [];
      const noResults = Boolean(obj.noMatch) || Boolean(obj.loginFailed);
      // Synthesize a single "$X total" row at the top so the source-card
      // summary line reads naturally even when line-extraction missed.
      const totalNum = (obj.totalOwed as number | null) ?? (obj.totalDue as number | null) ?? null;
      const synthRows: Array<Record<string, unknown>> = [];
      if (!noResults && totalNum !== null) {
        synthRows.push({ description: "TOTAL", amount: totalNum, _isTotal: true });
      }
      const rows = synthRows.concat(lines);
      return {
        totalRows: rows.length,
        noResults,
        rows,
        capturedAt: (obj.capturedAt as string) ?? row.completed_at ?? "",
      };
    }
    const totalRows = (obj.totalRows as number) ?? (obj.totalCases as number) ?? (obj.totalJudgments as number) ?? 0;
    const rows = (obj.rows as Array<Record<string, unknown>>) ??
      (obj.cases as Array<Record<string, unknown>>) ??
      (obj.judgments as Array<Record<string, unknown>>) ?? [];
    return {
      totalRows,
      noResults: Boolean(obj.noResults),
      rows,
      capturedAt: (obj.capturedAt as string) ?? row.completed_at ?? "",
    };
  } catch {
    return null;
  }
}

function SourceSummary({
  source,
  parsed,
  emptyMessage,
  njActivityDates,
}: {
  source: ExternalSource;
  parsed: ParsedResult;
  emptyMessage: string;
  njActivityDates?: Date[];
}): JSX.Element {
  if (parsed.noResults || parsed.totalRows === 0) {
    return <div className="text-[12px] opacity-75">{emptyMessage}</div>;
  }
  return (
    <div className="text-[12px]">
      <div className="font-semibold mb-1">
        {parsed.totalRows} {pluralize(source, parsed.totalRows)}
      </div>
      {/* DOC rows can carry a photoScreenshotId (server-uploaded mugshot).
          Render the thumbnails inline as a privacy-marked strip — internal
          only, never included in expungement packets or shareable links. */}
      {source === "doc" && parsed.rows.some((r) => r.photoScreenshotId) && (
        <div className="mb-2 flex flex-wrap items-end gap-2" title="Internal use only: not included in any client-facing report">
          {parsed.rows
            .filter((r) => r.photoScreenshotId)
            .slice(0, 6)
            .map((r, i) => {
              const sbi = (r.sbi as string) || "";
              const status = (r.status as string) || "";
              return (
                <a
                  key={i}
                  href={`/api/screenshots/${r.photoScreenshotId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                  title={`SBI ${sbi}${status ? ` — ${status}` : ""} (internal only)`}
                >
                  <img
                    src={`/api/screenshots/${r.photoScreenshotId}`}
                    alt=""
                    loading="lazy"
                    className="h-16 w-auto rounded border border-[color:var(--color-line)] opacity-90 hover:opacity-100 transition"
                    style={{ aspectRatio: "3/4", objectFit: "cover" }}
                  />
                </a>
              );
            })}
          <span className="meta self-center pl-1 text-[10px] uppercase tracking-[0.18em] text-[color:var(--color-text-dim)]">
            internal only
          </span>
        </div>
      )}
      <ul className="space-y-1">
        {parsed.rows.slice(0, 4).map((r, i) => {
          const summary = summarizeRow(source, r);
          return (
            <li key={i} className="opacity-90" title={summary}>
              <div className="truncate">• {summary}</div>
            </li>
          );
        })}
        {parsed.rows.length > 4 && (
          <li className="opacity-60 italic">…and {parsed.rows.length - 4} more</li>
        )}
      </ul>
    </div>
  );
}

function pluralize(source: ExternalSource, n: number): string {
  if (source === "doc") return n === 1 ? "match" : "matches";
  if (source === "pgpa") return n === 1 ? "case" : "cases";
  if (source === "mvc-surcharge") return n === 1 ? "surcharge line" : "surcharge lines";
  if (source === "mvc-restoration") return n === 1 ? "fee line" : "fee lines";
  return n === 1 ? "judgment" : "judgments";
}

function summarizeRow(source: ExternalSource, r: Record<string, unknown>): string {
  if (source === "mvc-surcharge" || source === "mvc-restoration") {
    const desc = (r.description as string) || "(line)";
    const amount = r.amount as number | undefined;
    const date = (r.suspensionDate as string) || (r.billDate as string) || "";
    const notice = (r.noticeNumber as string) || "";
    const isTotal = Boolean(r._isTotal);
    const amountStr =
      typeof amount === "number"
        ? `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : "";
    if (isTotal) return `TOTAL — ${amountStr || "amount unparsed"}`;
    return [desc, date, notice, amountStr].filter(Boolean).join(" — ") || "(unparsed row)";
  }
  if (source === "doc") {
    const sbi = r.sbi as string | undefined;
    const status = r.status as string | undefined;
    const facility = r.facility as string | undefined;
    // Time-served label is the most operationally useful single fact.
    // Falls back to the older status/facility view if not computable.
    const served = r.timeServedLabel as string | undefined;
    return [sbi && `SBI ${sbi}`, served || status, !served ? facility : null]
      .filter(Boolean)
      .join(" — ") || "(unparsed row)";
  }
  if (source === "pgpa") {
    const cn = r.caseNumber as string | undefined;
    const county = r.county as string | undefined;
    const status = r.status as string | undefined;
    return [cn, county, status].filter(Boolean).join(" — ") || "(unparsed row)";
  }
  // jocpa
  const docket = r.docketNumber as string | undefined;
  const type = r.judgmentType as string | undefined;
  const filed = r.filedAt as string | undefined;
  return [docket, type, filed].filter(Boolean).join(" — ") || "(unparsed row)";
}

